import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Box
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  DoneAll as DoneAllIcon,
  RemoveDone as RemoveDoneIcon
} from '@mui/icons-material';
import { generateDiff, formatValue, formatFieldName, DiffResult } from '../../../utils/diff';
import styles from './DiffReviewer.module.css';

interface DiffReviewerProps {
  isOpen: boolean;
  originalData: Record<string, any>;
  enrichedData: Record<string, any>;
  onApply: (finalData: Record<string, any>) => void;
  onCancel: () => void;
  title?: string;
}

interface ChangeState {
  status: 'accepted' | 'rejected';
  value: any; // The value to be applied (usually newValue, but can be edited)
  isEditing: boolean;
}

const DiffReviewer: React.FC<DiffReviewerProps> = ({
  isOpen,
  originalData,
  enrichedData,
  onApply,
  onCancel,
  title = "Review AI Enrichment"
}) => {
  const [changes, setChanges] = useState<Record<string, ChangeState>>({});
  const [diffs, setDiffs] = useState<DiffResult[]>([]);

  useEffect(() => {
    if (isOpen && originalData && enrichedData) {
      const calculatedDiffs = generateDiff(originalData, enrichedData).filter(d => d.hasChanged);
      setDiffs(calculatedDiffs);

      // Initialize all changes as accepted by default
      const initialChanges: Record<string, ChangeState> = {};
      calculatedDiffs.forEach(diff => {
        initialChanges[diff.field] = {
          status: 'accepted',
          value: diff.newValue,
          isEditing: false
        };
      });
      setChanges(initialChanges);
    }
  }, [isOpen, originalData, enrichedData]);

  const handleAccept = (field: string) => {
    setChanges((prev: Record<string, ChangeState>) => ({
      ...prev,
      [field]: { ...prev[field], status: 'accepted' }
    }));
  };

  const handleReject = (field: string) => {
    setChanges((prev: Record<string, ChangeState>) => ({
      ...prev,
      [field]: { ...prev[field], status: 'rejected' }
    }));
  };

  const handleToggleEdit = (field: string) => {
    setChanges((prev: Record<string, ChangeState>) => ({
      ...prev,
      [field]: { ...prev[field], isEditing: !prev[field].isEditing }
    }));
  };

  const handleEditChange = (field: string, newValue: string) => {
    setChanges((prev: Record<string, ChangeState>) => ({
      ...prev,
      [field]: { ...prev[field], value: newValue }
    }));
  };

  const handleAcceptAll = () => {
    setChanges((prev: Record<string, ChangeState>) => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        next[key].status = 'accepted';
      });
      return next;
    });
  };

  const handleRejectAll = () => {
    setChanges((prev: Record<string, ChangeState>) => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        next[key].status = 'rejected';
      });
      return next;
    });
  };

  const handleApply = () => {
    const finalData = { ...originalData };

    // Apply only accepted changes
    Object.keys(changes).forEach(field => {
      if (changes[field].status === 'accepted') {
        finalData[field] = changes[field].value;
      }
    });

    onApply(finalData);
  };

  const renderValue = (value: any) => {
    // Helper to render value nicely, especially arrays
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return formatValue(value);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {title}
          <Box>
            <Tooltip title="Reject All">
              <IconButton onClick={handleRejectAll} color="error" size="small">
                <RemoveDoneIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Accept All">
              <IconButton onClick={handleAcceptAll} color="success" size="small">
                <DoneAllIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {diffs.length === 0 ? (
          <div className={styles.emptyState}>
            <Typography>No changes found to review.</Typography>
          </div>
        ) : (
          <div className={styles.container}>
            {diffs.map((diff) => {
              const changeState = changes[diff.field];
              if (!changeState) return null;

              const isAccepted = changeState.status === 'accepted';
              const isEditing = changeState.isEditing;

              return (
                <div
                  key={diff.field}
                  className={`${styles.changeItem} ${isAccepted ? styles.accepted : styles.rejected}`}
                >
                  <div className={styles.header}>
                    <Typography className={styles.fieldName}>
                      {formatFieldName(diff.field)}
                    </Typography>
                    <div className={styles.actions}>
                      {isEditing ? (
                        <Button
                          size="small"
                          onClick={() => handleToggleEdit(diff.field)}
                          startIcon={<CheckIcon />}
                        >
                          Done
                        </Button>
                      ) : (
                        <>
                          <Tooltip title="Edit Value">
                            <IconButton size="small" onClick={() => handleToggleEdit(diff.field)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isAccepted ? (
                            <Tooltip title="Reject Change">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleReject(diff.field)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Accept Change">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleAccept(diff.field)}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className={styles.diffContainer}>
                    <div className={styles.oldValue}>
                      {renderValue(diff.oldValue)}
                    </div>
                    <div className={styles.arrow}>→</div>
                    <div className={styles.newValue}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          size="small"
                          value={changeState.value}
                          onChange={(e) => handleEditChange(diff.field, e.target.value)}
                          variant="outlined"
                        />
                      ) : (
                        renderValue(changeState.value)
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          startIcon={<CheckIcon />}
        >
          Apply {Object.values(changes).filter(c => c.status === 'accepted').length} Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiffReviewer;


